import { Nodes } from 'mdast';
import { FormattedTextToken } from '../FormattedText/props';

/**
 * Puts the target of a link back the way it was written.
 *
 * `encodeTokenPlaceholders` swaps every URL the backend found for a single placeholder character,
 * and the backend finds the target of `[label](https://…)` like any other URL. So the parser reads
 * `[label]()` and links to the placeholder itself — percent-encoded, `%EE%80%80`, which the
 * browser resolves as a path on the app's own origin.
 *
 * `rehypeTokenPlaceholders` cannot undo that: it splits text, and a target is an attribute that is
 * already percent-encoded by the time it gets there. This runs on the markdown tree instead, before
 * a link becomes an element, and writes each placeholder back as the URL it stood for. The
 * sanitiser still runs afterwards, so a restored target is checked like any other.
 */
export function remarkLinkPlaceholders(options: {
  tokenByCharCode: Map<number, FormattedTextToken>;
}) {
  return (tree: Nodes) => {
    if (options.tokenByCharCode.size > 0) {
      restoreLinkTargets(tree, options.tokenByCharCode);
    }
  };
}

function restoreLinkTargets(node: Nodes, tokenByCharCode: Map<number, FormattedTextToken>) {
  // A reference link (`[label][ref]`) takes its target from the definition it names.
  if (node.type === 'link' || node.type === 'definition') {
    node.url = restorePlaceholders(node.url, tokenByCharCode);
    if (node.title) {
      node.title = restorePlaceholders(node.title, tokenByCharCode);
    }
  }

  if ('children' in node) {
    node.children.forEach(child => restoreLinkTargets(child, tokenByCharCode));
  }
}

function restorePlaceholders(value: string, tokenByCharCode: Map<number, FormattedTextToken>) {
  let out = '';
  for (const character of value) {
    out += tokenByCharCode.get(character.codePointAt(0)!)?.value ?? character;
  }
  return out;
}
